// @ts-check
/**
 * @typedef {'shared' | 'aws' | 'ms'} FieldType
 * @typedef {'account' | 'report' | 'list' | 'all'} FieldContext
 * @typedef {'basic' | 'aws_specific' | 'ms_specific' | 'business' | 'financial' | 'technical' | 'relationship'} FieldCategory
 *
 * @typedef {Object} DisplayField
 * @property {string} label - Human-readable label
 * @property {string} field - Internal key
 * @property {FieldType} type - Data source
 * @property {FieldContext[]} contexts - Where this field is used
 * @property {FieldCategory} category - Field category for grouping
 * @property {boolean} [isDefault] - Whether this field is shown by default
 */

/** @type {DisplayField[]} */
 export const displayFields = [
    // Basic Information
    {
        label: "Account ID",
        field: "account_id",
        type: "shared",
        contexts: ["all"],
        category: "basic"    },
    {
        label: "Searched Account Name",
        field: "original_name",
        type: "shared",
        contexts: ["list"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Account Name",
        field: "account_name",
        type: "shared",
        contexts: ["all"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Website",
        field: "website",
        type: "shared",
        contexts: ["all"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Address",
        field: "address",
        type: "shared",
        contexts: ["all"],
        category: "basic",
        isDefault: true
    },
    {
        label: "City",
        field: "city",
        type: "shared",
        contexts: ["all"],
        category: "basic"
    },
    {
        label: "State",
        field: "state",
        type: "shared",
        contexts: ["all"],
        category: "basic"
    },
    {
        label: "HQ",
        field: "hq",
        type: "shared",
        contexts: ["all"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Region",
        field: "region",
        type: "shared",
        contexts: ["all"],
        category: "basic"
    },
    {
        label: "US District",
        field: "us_district",
        type: "shared",
        contexts: ["all"],
        category: "basic"
    },

    // Company Information
    {
        label: "Industry",
        field: "industry",
        type: "shared",
        contexts: ["all"],
        category: "business",
        isDefault: true
    },
    {
        label: "Organization Type",
        field: "organization_type",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Employees",
        field: "employees",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Founded",
        field: "founded",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Slogan",
        field: "slogan",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Specialties",
        field: "specialties",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Account Description",
        field: "account_description",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },

    // Social & Online Presence
    {
        label: "LinkedIn URL",
        field: "linkedin_url",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "LinkedIn Followers",
        field: "linkedin_followers",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Company LinkedIn ID",
        field: "company_linkedin_id",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Crunchbase URL",
        field: "crunchbase_url",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Logo",
        field: "logo",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },

    // Business Relationships
    {
        label: "Subsidiaries",
        field: "subsidiaries",
        type: "shared",
        contexts: ["all"],
        category: "relationship"
    },
    {
        label: "Affiliates",
        field: "affiliates",
        type: "shared",
        contexts: ["all"],
        category: "relationship"
    },
    {
        label: "Similar Accounts",
        field: "similar_accounts",
        type: "shared",
        contexts: ["all"],
        category: "relationship"
    },

    // Financial Information
    {
        label: "Funding",
        field: "funding",
        type: "shared",
        contexts: ["all"],
        category: "financial"
    },
    {
        label: "Investors",
        field: "investors",
        type: "shared",
        contexts: ["all"],
        category: "financial"
    },
    {
        label: "Fortune Num",
        field: "fortune_num",
        type: "shared",
        contexts: ["all"],
        category: "financial"
    },
    {
        label: "Duns Num",
        field: "duns_num",
        type: "shared",
        contexts: ["all"],
        category: "financial"
    },

    // Technical Stack
    {
        label: "Installed ISV Technologies",
        field: "installed_isv_technologies",
        type: "shared",
        contexts: ["all"],
        category: "technical"
    },
    {
        label: "Installed Enterprise Technologies",
        field: "installed_enterprise_technologies",
        type: "shared",
        contexts: ["all"],
        category: "technical"
    },
    {
        label: "Installed Infra Providers",
        field: "installed_infra_providers",
        type: "shared",
        contexts: ["all"],
        category: "technical"
    },
    {
        label: "Installed Developer Tools",
        field: "installed_developer_tools",
        type: "shared",
        contexts: ["all"],
        category: "technical"
    },

    // AWS Specific Fields
    {
        label: "AWS Account ID",
        field: "aws_account_id",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Subsidiary",
        field: "aws_subsidiary",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Segment",
        field: "aws_segment",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Sub-Segment",
        field: "aws_sub_segment",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Partner",
        field: "aws_partner",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "Partner Type",
        field: "partner_type",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "ISV Accelerate",
        field: "isv_accelerate",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "ACE Eligible",
        field: "ace_eligible",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "SaaS Revenue Recognition",
        field: "saas_revenue_recognition",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Marketplace Status",
        field: "aws_marketplace_status",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Adoption",
        field: "aws_adoption",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "Engaged Greenfield",
        field: "engaged_greenfield",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Services",
        field: "aws_services",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Code",
        field: "aws_code",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Partner Status",
        field: "aws_partner_status",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Relationship Strength",
        field: "aws_relationship_strength",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },
    {
        label: "AWS Main Contacts",
        field: "aws_main_contacts",
        type: "aws",
        contexts: ["all"],
        category: "aws_specific"
    },

    // Microsoft Specific Fields
    {
        label: "Microsoft Name",
        field: "microsoft_name",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Microsoft Subsidiary",
        field: "microsoft_subsidiary",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Microsoft Segment Group",
        field: "microsoft_segment_group",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Microsoft Sub-Segment",
        field: "microsoft_sub_segment",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Microsoft Partner",
        field: "microsoft_partner",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "IP Co-Sell",
        field: "ip_co_sell",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "PR-ACR",
        field: "pr_acr",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Top-Tier",
        field: "top_tier",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Microsoft Marketplace Status",
        field: "microsoft_marketplace_status",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "MACC Eligible",
        field: "macc_eligible",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Azure Adoption",
        field: "azure_adoption",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Microsoft Relationship Strength",
        field: "microsoft_relationship_strength",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Microsoft Main Contacts",
        field: "microsoft_main_contacts",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Microsoft Services",
        field: "microsoft_services",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },
    {
        label: "Microsoft Tags",
        field: "microsoft_tags",
        type: "ms",
        contexts: ["all"],
        category: "ms_specific"
    },

    // Metadata Fields
    {
        label: "Initial Source",
        field: "initial_source",
        type: "shared",
        contexts: ["all"],
        category: "basic"
    },
    {
        label: "Latest Source",
        field: "latest_source",
        type: "shared",
        contexts: ["all"],
        category: "basic"
    },
    {
        label: "Timestamp",
        field: "timestamp",
        type: "shared",
        contexts: ["all"],
        category: "basic"
    },
    {
        label: "Decision Making",
        field: "decision_making",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    {
        label: "Presence",
        field: "presence",
        type: "shared",
        contexts: ["all"],
        category: "business"
    },
    // Contact Information
    {
        label: "Contact Name",
        field: "contact_name",
        type: "shared",
        contexts: ["list", "report"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Contact Email",
        field: "contact_email",
        type: "shared",
        contexts: ["list", "report"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Annual Revenue",
        field: "annual_revenue",
        type: "shared",
        contexts: ["list", "report"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Match Type",
        field: "match_type",
        type: "shared",
        contexts: ["list"],
        category: "basic",
        isDefault: true
    },
    {
        label: "Role Category",
        field: "role_category",
        type: "shared",
        contexts: ["list", "report"],
        category: "basic"
    },
    {
        label: "Role Segment",
        field: "segment_name",
        type: "shared",
        contexts: ["list", "report"],
        category: "basic"
    }
];

/**
 * @param {string[]} keys - Field keys from backend
 * @param {DisplayField[]} allFields - Full field config list
 * @param {'aws' | 'ms'} vendor - Current vendor context
 * @returns {DisplayField[]} - Ordered, vendor-matching fields
 */
// export function getDisplayFieldsByOrder(keys, allFields, vendor) {
//     // Validate that all requested keys exist in allFields
//     const invalidKeys = keys.filter(key => !allFields.some(f => f.field === key));
//     if (invalidKeys.length > 0) {
//         console.error('Invalid field keys detected:', {
//             invalidKeys,
//             availableFields: allFields.map(f => f.field)
//         });
//         throw new Error(`Invalid field keys detected: ${invalidKeys.join(', ')}`);
//     }

//     return /** @type {DisplayField[]} */ (
//       keys
//         .map(key =>
//           allFields.find(
//             f => f.field === key && (f.type === 'shared' || f.type === vendor)
//           )
//         )
//         .filter(Boolean)
//     );
//   }
  
/**
 * Get fields by category
 * @param {DisplayField[]} fields - All fields
 * @param {FieldCategory} category - Category to filter by
 * @returns {DisplayField[]} - Fields in the specified category
 */
export function getFieldsByCategory(fields, category) {
    return fields.filter(field => field.category === category);
}

/**
 * Get default fields for a vendor
 * @returns {DisplayField[]} - Default fields for the vendor
 */
// export function getDefaultFields(vendor) {
//     return displayFields.filter(field =>
//         field.isDefault && (field.type === 'shared' || field.type === vendor)
//     );
// }
export const getDefaultFields = () => displayFields.filter(f => f.isDefault);
/**
 * Get display fields filtered by vendor and optional keys.
//  * @param {'aws' | 'ms'} vendor
//  * @param {string[] | null} [keys]
//  * @returns {DisplayField[]}
//  */
export const getDisplayFields = (vendor, isDefault=false, context, keys = null) => {
  const allowed = displayFields.filter(f =>
    f.type === 'shared' || f.type === vendor
  ).filter(f => isDefault ? f.isDefault : true)
    .filter(f => {
      if (!context) return true;
      return f.contexts.includes(context) || f.contexts.includes('all');
    });
//   if no context return true
  // .filter(f => f.contexts.includes(context) || f.contexts.includes('all'));

//   .filter(f => f.contexts.includes(context) || f.contexts.includes('all'));

  if (!keys||keys.length==0) return allowed;

  return keys
    .map(key => allowed.find(f => f.field === key))
    .filter(Boolean); // removes undefined if a key isn't found
};

/**
 * Get fields by context
 * @param {DisplayField[]} fields - All fields
 * @param {FieldContext} context - Context to filter by
 * @returns {DisplayField[]} - Fields for the specified context
 */
export function getFieldsByContext(fields, context) {
    return fields.filter(field =>
        field.contexts.includes(context) || field.contexts.includes('all')
    );
}
